<template>
  <div class="c_date_filter">
    <div class="datepicker-trigger">
      <FormLabel input-id="datepicker-trigger" :label-text="label" />
      <div class="c_box--pos--relative">
        <client-only>
          <input
            type="text"
            id="datepicker-trigger"
            :placeholder="$t('Select dates')"
            :value="formatDates(startDate, endDate)"
          />
          <AirbnbStyleDatepicker
            :date-one="startDate"
            :date-two="endDate"
            :fullscreen-mobile="true"
            :months-to-show="1"
            :show-shortcuts-menu-trigger="false"
            :trigger-element-id="'datepicker-trigger'"
            @opened="onDatePickerOpened"
            @date-one-selected="
              (val) => {
                startDate = endDate = val;
                onDatePickerUpdate();
              }
            "
            @date-two-selected="
              (val) => {
                endDate = val;
                onDatePickerUpdate();
              }
            "
            :aria-labels="{
              chooseDate: (date) => date,
              chooseStartDate: (date) => `${$t('Choose')} ${date} ${$t('as your start date.')}`,
              chooseEndDate: (date) => `${$t('Choose')} ${date} ${$t('as your end date.')}`,
              selectedDate: (date) => `${$t('Selected.')} ${date}`,
              unavailableDate: (date) => `${$t('Not available.')} ${date}`,
              previousMonth: $t('Move backward to switch to the previous month.'),
              nextMonth: $t('Move forward to switch to the next month.'),
              closeDatepicker: $t('Close calendar'),
              openKeyboardShortcutsMenu: $t('Open keyboard shortcuts menu.'),
              closeKeyboardShortcutsMenu: $t('Close keyboard shortcuts menu'),
            }"
            :keyboard-shortcuts="[
              { symbol: '↵', label: $t('Select the date in focus'), symbolDescription: $t('Enter key') },
              {
                symbol: '←/→',
                label: $t('Move backward (left) and forward (right) by one day.'),
                symbolDescription: $t('Left or right arrow keys'),
              },
              {
                symbol: '↑/↓',
                label: $t('Move backward (up) and forward (down) by one week.'),
                symbolDescription: $t('Up or down arrow keys'),
              },
              { symbol: 'PgUp/PgDn', label: $t('Switch months.'), symbolDescription: $t('PageUp and PageDown keys') },
              {
                symbol: 'Home/End',
                label: $t('Go to the first or last day of a week.'),
                symbolDescription: $t('Home or End keys'),
              },
              { symbol: 'Esc', label: $t('Close this panel'), symbolDescription: $t('Escape key') },
              { symbol: '?', label: $t('Open this panel'), symbolDescription: $t('Question mark') },
            ]"
          />
        </client-only>
      </div>
    </div>
  </div>
</template>

<script>
import { log } from 'JS/global.js';
import format from 'date-fns/format';
import addDays from 'date-fns/add_days';
import startOfWeek from 'date-fns/start_of_week';
import endOfWeek from 'date-fns/end_of_week';
import startOfMonth from 'date-fns/start_of_month';
import endOfMonth from 'date-fns/end_of_month';
import FormLabel from 'Components/form/FormLabel.vue';

export default {
  components: {
    FormLabel,
  },
  data() {
    return {
      dateFormat: 'YYYY-MM-DD',
      startDate: '',
      endDate: '',
      displayDateFormat: 'D MMM',
    };
  },
  props: {
    // itemsToFilter: { type: Array, required: true },
    labelText: String,
  },
  computed: {
    label() {
      return this.labelText || this.$t('Pick a date');
    },
  },
  methods: {
    formatDates(dateOne, dateTwo) {
      let formattedDates = '';
      if (dateOne) {
        formattedDates = format(dateOne, this.displayDateFormat);
      }
      if (dateTwo) {
        formattedDates += ' - ' + format(dateTwo, this.displayDateFormat);
      }
      return formattedDates;
    },
    onDatePickerOpened() {
      this.$emit('datePickerOpened');
    },
    onDatePickerUpdate() {
      this.$emit('datePickerUpdated', this.startDate, this.endDate);
    },
    resetDates() {
      this.startDate = this.endDate = '';
    },
    selectToday() {
      const today = new Date();
      const formattedDate = format(today, this.dateFormat);
      log('Selecting Today', formattedDate);
      this.startDate = this.endDate = formattedDate;
    },
    selectThisWeek() {
      const today = new Date();
      const firstDay = today.getDay() === 0 ? startOfWeek(today) : addDays(startOfWeek(today), 1);
      const lastDay = today.getDay() === 0 ? endOfWeek(today) : addDays(endOfWeek(today), 1);
      log('Selecting This Week', firstDay, lastDay);
      this.startDate = format(firstDay);
      this.endDate = format(lastDay);
    },
    selectNextWeek() {
      const nextWeek = addDays(new Date(), 7);
      const firstDay = nextWeek.getDay() === 0 ? startOfWeek(nextWeek) : addDays(startOfWeek(nextWeek), 1);
      const lastDay = nextWeek.getDay() === 0 ? endOfWeek(nextWeek) : addDays(endOfWeek(nextWeek), 1);
      log('Selecting Next Week', firstDay, lastDay);
      this.startDate = format(firstDay);
      this.endDate = format(lastDay);
    },
    selectThisMonth() {
      const today = new Date();
      const firstDay = startOfMonth(today);
      const lastDay = endOfMonth(today);
      log('Selecting This Month', firstDay, lastDay);
      this.startDate = format(firstDay);
      this.endDate = format(lastDay);
    },
    selectNextMonth() {
      const today = new Date();
      const firstDay = addDays(endOfMonth(today), 1);
      const lastDay = endOfMonth(firstDay);
      log('Selecting Next Month', firstDay, lastDay);
      this.startDate = format(firstDay);
      this.endDate = format(lastDay);
    },
  },
  created() {},
  mounted() {},
};
</script>

<style lang="scss">
.c_date_filter {
  $self: &;

  input {
    width: 100%;
  }
}
</style>
